import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Church, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ethical social proof: shows ONLY real, permission-approved activity from the
// backend. No fabricated data — renders nothing until such activity exists.
export const SocialProof = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    axios.get(`${API}/social-proof`).then((r) => {
      if (Array.isArray(r.data)) setItems(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (dismissed || items.length === 0) return;
    const show = () => {
      setVisible(true);
      timers.current.push(setTimeout(() => setVisible(false), 6000));
      timers.current.push(setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        show();
      }, 14000));
    };
    const start = setTimeout(show, 4000);
    return () => {
      clearTimeout(start);
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [items, dismissed]);

  if (dismissed || items.length === 0) return null;
  const item = items[idx];

  return (
    <div className="fixed bottom-4 left-4 z-40 max-w-[calc(100vw-2rem)] sm:max-w-xs" aria-live="polite">
      <AnimatePresence>
        {visible && item && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
            className="flex items-start gap-3 rounded-2xl bg-white border border-navy-100 shadow-xl p-4 pr-9"
            data-testid="social-proof-toast"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-500/15 text-gold-600">
              <Church size={18} />
            </span>
            <div className="text-sm">
              <p className="font-semibold text-navy-900">{item.title}</p>
              {item.subtitle && <p className="text-charcoal-700/70 text-xs mt-0.5">{item.subtitle}</p>}
            </div>
            <button
              onClick={() => setDismissed(true)}
              aria-label="Dismiss notification"
              className="absolute right-2 top-2 p-1.5 text-charcoal-700/50 hover:text-navy-900 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
            >
              <X size={15} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
